import React, { useState, useEffect } from 'react';
import { 
  Share2, 
  Lock, 
  Unlock, 
  Eye, 
  EyeOff, 
  Download, 
  Upload, 
  Users, 
  UserCheck, 
  UserX,
  Shield,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Calendar,
  FileText,
  Database,
  Key,
  Fingerprint,
  Globe,
  Building,
  Heart,
  Activity,
  Brain,
  Target,
  Settings,
  RefreshCw,
  Plus,
  Edit,
  Trash2,
  Search,
  Filter
} from 'lucide-react';

interface DataShare {
  id: string;
  title: string;
  description: string;
  type: 'health-record' | 'lab-results' | 'medications' | 'vitals' | 'symptoms' | 'fitness';
  recipient: string;
  recipientType: 'provider' | 'family' | 'researcher' | 'insurance';
  status: 'pending' | 'active' | 'expired' | 'revoked';
  permissions: string[];
  dataIncluded: string[];
  startDate: Date;
  endDate?: Date;
  lastAccessed?: Date;
  accessCount: number;
  isEncrypted: boolean;
  isHIPAACompliant: boolean;
  consentGiven: boolean;
  auditTrail: Array<{
    timestamp: Date;
    action: string;
    user: string;
    details: string;
  }>;
}

interface DataSharingProps {
  onBack?: () => void;
}

export const DataSharing: React.FC<DataSharingProps> = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'shares' | 'requests' | 'settings'>('overview');
  const [dataShares, setDataShares] = useState<DataShare[]>([]);
  const [selectedShare, setSelectedShare] = useState<DataShare | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Mock data shares
  useEffect(() => {
    const mockShares: DataShare[] = [
      {
        id: '1',
        title: 'Diabetes Management Data',
        description: 'Comprehensive diabetes management data including blood glucose, medications, and lifestyle factors',
        type: 'health-record',
        recipient: 'Dr. Sarah Johnson',
        recipientType: 'provider',
        status: 'active',
        permissions: ['read', 'download'],
        dataIncluded: ['blood-glucose', 'medications', 'exercise', 'diet'],
        startDate: new Date('2025-01-01'),
        endDate: new Date('2025-12-31'),
        lastAccessed: new Date('2025-01-14'),
        accessCount: 12,
        isEncrypted: true,
        isHIPAACompliant: true,
        consentGiven: true,
        auditTrail: [
          {
            timestamp: new Date('2025-01-14T10:30:00'),
            action: 'data_accessed',
            user: 'Dr. Sarah Johnson',
            details: 'Viewed blood glucose trends'
          },
          {
            timestamp: new Date('2025-01-10T14:15:00'),
            action: 'data_downloaded',
            user: 'Dr. Sarah Johnson',
            details: 'Downloaded medication history'
          }
        ]
      },
      {
        id: '2',
        title: 'Lab Results for Research',
        description: 'Anonymized lab results for cardiovascular health research study',
        type: 'lab-results',
        recipient: 'Cardiovascular Research Institute',
        recipientType: 'researcher',
        status: 'active',
        permissions: ['read'],
        dataIncluded: ['cholesterol', 'blood-pressure', 'heart-rate'],
        startDate: new Date('2025-01-05'),
        endDate: new Date('2025-06-30'),
        lastAccessed: new Date('2025-01-12'),
        accessCount: 3,
        isEncrypted: true,
        isHIPAACompliant: true,
        consentGiven: true,
        auditTrail: [
          {
            timestamp: new Date('2025-01-12T09:45:00'),
            action: 'data_accessed',
            user: 'Research Team',
            details: 'Analyzed cholesterol trends'
          }
        ]
      },
      {
        id: '3',
        title: 'Family Health Summary',
        description: 'Basic health summary for family emergency contacts',
        type: 'vitals',
        recipient: 'Emergency Contact - John Smith',
        recipientType: 'family',
        status: 'active',
        permissions: ['read'],
        dataIncluded: ['allergies', 'medications', 'blood-type'],
        startDate: new Date('2025-01-01'),
        lastAccessed: new Date('2025-01-08'),
        accessCount: 1,
        isEncrypted: true,
        isHIPAACompliant: true,
        consentGiven: true,
        auditTrail: [
          {
            timestamp: new Date('2025-01-08T16:20:00'),
            action: 'data_accessed',
            user: 'John Smith',
            details: 'Viewed emergency contact info'
          }
        ]
      }
    ];
    setDataShares(mockShares);
  }, []);

  const filteredShares = dataShares.filter(share => {
    const matchesSearch = share.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         share.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         share.recipient.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === 'all' || share.type === typeFilter;
    const matchesStatus = statusFilter === 'all' || share.status === statusFilter;
    
    return matchesSearch && matchesType && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'expired': return 'bg-red-100 text-red-800';
      case 'revoked': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'health-record': return 'bg-blue-100 text-blue-800';
      case 'lab-results': return 'bg-purple-100 text-purple-800';
      case 'medications': return 'bg-orange-100 text-orange-800';
      case 'vitals': return 'bg-green-100 text-green-800';
      case 'symptoms': return 'bg-red-100 text-red-800';
      case 'fitness': return 'bg-pink-100 text-pink-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRecipientIcon = (type: string) => {
    switch (type) {
      case 'provider': return <UserCheck className="h-5 w-5" />;
      case 'family': return <Users className="h-5 w-5" />;
      case 'researcher': return <Building className="h-5 w-5" />;
      case 'insurance': return <Shield className="h-5 w-5" />;
      default: return <Users className="h-5 w-5" />;
    }
  };

  const shareStats = {
    total: dataShares.length,
    active: dataShares.filter(s => s.status === 'active').length,
    pending: dataShares.filter(s => s.status === 'pending').length,
    expired: dataShares.filter(s => s.status === 'expired').length,
    totalAccess: dataShares.reduce((sum, s) => sum + s.accessCount, 0),
    encrypted: dataShares.filter(s => s.isEncrypted).length,
    hipaaCompliant: dataShares.filter(s => s.isHIPAACompliant).length
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Secure Data Sharing</h1>
          <p className="text-slate-600">HIPAA-compliant secure data sharing between providers and patients</p>
        </div>
        <div className="flex items-center space-x-3">
          {onBack && (
            <button
              onClick={onBack}
              className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 rounded-lg transition-colors"
            >
              ← Back
            </button>
          )}
          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>Share Data</span>
          </button>
        </div>
      </div>

      {/* Security Status */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Total Shares</p>
              <p className="text-2xl font-bold text-slate-800">{shareStats.total}</p>
            </div>
            <Share2 className="h-8 w-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Active Shares</p>
              <p className="text-2xl font-bold text-slate-800">{shareStats.active}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Total Access</p>
              <p className="text-2xl font-bold text-slate-800">{shareStats.totalAccess}</p>
            </div>
            <Eye className="h-8 w-8 text-purple-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Encrypted</p>
              <p className="text-2xl font-bold text-slate-800">{shareStats.encrypted}</p>
            </div>
            <Lock className="h-8 w-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">HIPAA Compliant</p>
              <p className="text-2xl font-bold text-slate-800">{shareStats.hipaaCompliant}</p>
            </div>
            <Shield className="h-8 w-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Pending</p>
              <p className="text-2xl font-bold text-slate-800">{shareStats.pending}</p>
            </div>
            <Clock className="h-8 w-8 text-yellow-500" />
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="border-b border-slate-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('shares')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'shares'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              Data Shares
            </button>
            <button
              onClick={() => setActiveTab('requests')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'requests'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              Access Requests
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'settings'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              Settings
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-slate-800">Data Sharing Overview</h3>
              
              {/* Security Status */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-100">Security Status</p>
                      <p className="text-3xl font-bold">SECURE</p>
                    </div>
                    <Shield className="h-12 w-12 text-green-200" />
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100">HIPAA Compliance</p>
                      <p className="text-3xl font-bold">100%</p>
                    </div>
                    <CheckCircle className="h-12 w-12 text-blue-200" />
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100">Active Shares</p>
                      <p className="text-3xl font-bold">{shareStats.active}</p>
                    </div>
                    <Share2 className="h-12 w-12 text-purple-200" />
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              <div>
                <h4 className="font-semibold text-slate-800 mb-4">Recent Activity</h4>
                <div className="space-y-3">
                  {dataShares
                    .filter(s => s.lastAccessed)
                    .sort((a, b) => (b.lastAccessed?.getTime() || 0) - (a.lastAccessed?.getTime() || 0))
                    .slice(0, 3)
                    .map((share) => (
                      <div key={share.id} className="bg-slate-50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            {getRecipientIcon(share.recipientType)}
                            <div>
                              <h5 className="font-medium text-slate-800">{share.title}</h5>
                              <p className="text-sm text-slate-600">
                                Last accessed: {share.lastAccessed?.toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(share.status)}`}>
                            {share.status}
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'shares' && (
            <div className="space-y-6">
              {/* Filters */}
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Search shares..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Types</option>
                  <option value="health-record">Health Records</option>
                  <option value="lab-results">Lab Results</option>
                  <option value="medications">Medications</option>
                  <option value="vitals">Vitals</option>
                  <option value="symptoms">Symptoms</option>
                  <option value="fitness">Fitness</option>
                </select>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="expired">Expired</option>
                  <option value="revoked">Revoked</option>
                </select>
              </div>

              {/* Shares List */}
              <div className="space-y-4">
                {filteredShares.map((share) => (
                  <div key={share.id} className="bg-white border border-slate-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-lg font-semibold text-slate-800 mb-2">{share.title}</h3>
                            <p className="text-slate-600 mb-3">{share.description}</p>
                            
                            <div className="flex items-center space-x-4 mb-3">
                              <div className="flex items-center space-x-2">
                                {getRecipientIcon(share.recipientType)}
                                <span className="text-sm text-slate-600">{share.recipient}</span>
                              </div>
                              <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(share.status)}`}>
                                {share.status}
                              </span>
                              <span className={`px-2 py-1 text-xs rounded-full ${getTypeColor(share.type)}`}>
                                {share.type.replace('-', ' ')}
                              </span>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3 text-sm text-slate-500">
                              <div>
                                <span className="font-medium">Start:</span> {formatDate(share.startDate)}
                              </div>
                              {share.endDate && (
                                <div>
                                  <span className="font-medium">End:</span> {formatDate(share.endDate)}
                                </div>
                              )}
                              <div>
                                <span className="font-medium">Access:</span> {share.accessCount} times
                              </div>
                              <div>
                                <span className="font-medium">Last:</span> {share.lastAccessed ? formatDate(share.lastAccessed) : 'Never'}
                              </div>
                            </div>

                            <div className="mb-3">
                              <h4 className="font-medium text-slate-800 mb-2">Data Included:</h4>
                              <div className="flex flex-wrap gap-2">
                                {share.dataIncluded.map((item, index) => (
                                  <span key={index} className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
                                    {item.replace('-', ' ')}
                                  </span>
                                ))}
                              </div>
                            </div>

                            <div className="flex items-center space-x-4 text-sm">
                              {share.isEncrypted && (
                                <span className="flex items-center text-green-600">
                                  <Lock className="h-4 w-4 mr-1" />
                                  Encrypted
                                </span>
                              )}
                              {share.isHIPAACompliant && (
                                <span className="flex items-center text-blue-600">
                                  <Shield className="h-4 w-4 mr-1" />
                                  HIPAA Compliant
                                </span>
                              )}
                              {share.consentGiven && (
                                <span className="flex items-center text-green-600">
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Consent Given
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
                          <Eye className="h-4 w-4" />
                          <span>View Details</span>
                        </button>
                        
                        <div className="flex flex-col space-y-1">
                          <button className="p-2 text-slate-400 hover:text-slate-600 transition-colors">
                            <Edit className="h-4 w-4" />
                          </button>
                          <button className="p-2 text-slate-400 hover:text-red-600 transition-colors">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {filteredShares.length === 0 && (
                  <div className="text-center py-12">
                    <Share2 className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-800 mb-2">No data shares found</h3>
                    <p className="text-slate-600">No data shares match your current filters.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'requests' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-slate-800">Access Requests</h3>
              
              <div className="bg-white rounded-lg p-6 border border-slate-200">
                <div className="text-center py-8">
                  <UserCheck className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">No Pending Requests</h3>
                  <p className="text-slate-600">You have no pending data access requests at this time.</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-slate-800">Data Sharing Settings</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg p-6 border border-slate-200">
                  <h4 className="font-medium text-slate-800 mb-4">Privacy Settings</h4>
                  <div className="space-y-4">
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" defaultChecked className="rounded" />
                      <span className="text-sm text-slate-700">Require consent for all shares</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" defaultChecked className="rounded" />
                      <span className="text-sm text-slate-700">Encrypt all shared data</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" defaultChecked className="rounded" />
                      <span className="text-sm text-slate-700">Audit all access attempts</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" className="rounded" />
                      <span className="text-sm text-slate-700">Allow anonymous research access</span>
                    </label>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6 border border-slate-200">
                  <h4 className="font-medium text-slate-800 mb-4">Notification Settings</h4>
                  <div className="space-y-4">
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" defaultChecked className="rounded" />
                      <span className="text-sm text-slate-700">Data access notifications</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" defaultChecked className="rounded" />
                      <span className="text-sm text-slate-700">Share expiration reminders</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" defaultChecked className="rounded" />
                      <span className="text-sm text-slate-700">Security alerts</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" className="rounded" />
                      <span className="text-sm text-slate-700">Weekly access reports</span>
                    </label>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6 border border-slate-200">
                  <h4 className="font-medium text-slate-800 mb-4">Default Permissions</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Default Share Duration</label>
                      <select className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                        <option value="7">7 days</option>
                        <option value="30" selected>30 days</option>
                        <option value="90">90 days</option>
                        <option value="365">1 year</option>
                        <option value="0">No expiration</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Default Access Level</label>
                      <select className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                        <option value="read">Read only</option>
                        <option value="download" selected>Read and download</option>
                        <option value="full">Full access</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6 border border-slate-200">
                  <h4 className="font-medium text-slate-800 mb-4">Security Settings</h4>
                  <div className="space-y-4">
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" defaultChecked className="rounded" />
                      <span className="text-sm text-slate-700">Two-factor authentication</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" defaultChecked className="rounded" />
                      <span className="text-sm text-slate-700">Session timeout</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" defaultChecked className="rounded" />
                      <span className="text-sm text-slate-700">IP address logging</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" className="rounded" />
                      <span className="text-sm text-slate-700">Advanced encryption</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex space-x-4">
                <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors">
                  Save Settings
                </button>
                <button className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-6 py-2 rounded-lg transition-colors">
                  Reset to Default
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataSharing; 